import {
    AppDeviceErrorType,
    AppDeviceMessageAck,
    AppDeviceService,
    ReceivedMessageType,
    SentMessageType
} from '../../app-device/app-device.service';
import {
    StripeConnectionTokenRefreshRequestData,
    StripeConnectionTokenRefreshService
} from "./stripe-connection-token-refresh.service";
import {computed, inject, Injectable, Signal} from "@angular/core";

import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {EntityData} from "@fedelta-pos/data-client";
import {AuthorisedPaymentEntityModel} from "./authorised-payment-entity.model";
import {TapToPayIntentCreationService, TapToPayPaymentAuthorisationData} from "./tap-to-pay-intent-creation.service";
import {PaymentAuthorisationData} from '../../order/order-placement.service';
import {NotificationService} from "../../notification/notification.service";
import {ConfigurationModel} from '../../configuration/configuration.model';
import {DeferredInjectable} from '../../deferred-injection.service';

// noinspection JSUnusedGlobalSymbols
export enum TapToPayErrorType {
    DEVICE_INITIALISATION_ERROR = "device-initialisation-error",
    PROVIDER_INITIALISATION_ERROR = "provider-initialisation-error",
    READER_DISCOVERY_ERROR = "reader-discovery-error",
    READER_CONNECTION_ERROR = "reader-connection-error"
}

/**
 * Unsolicited payment error information from the app device
 */
export class TapToPayError {
    public errorType: TapToPayErrorType;
    public errorMessage: string;
    // noinspection JSUnusedGlobalSymbols
    public providerErrorCode: string;
    // noinspection JSUnusedGlobalSymbols
    public providerErrorMessage: string;
}

export class TapToPayIntent {
    public paymentIntentId: string;
    // noinspection JSUnusedGlobalSymbols
    public clientSecret: string;
}

export class AuthorisedTapToPayPayment {
    public paymentIntentId: string;
    // noinspection JSUnusedGlobalSymbols
    public integratedPaymentConfigId: number;
    // noinspection JSUnusedGlobalSymbols
    public platformLiveMode: boolean;
    public amount: number;
    public paymentCardBrand: string;
    public paymentCardCountry: string;
    public paymentCardLast4: string;
    public paymentCardNetwork: string;
    // noinspection JSUnusedGlobalSymbols
    public paymentCardHolderName: string;
}

/**
 * When running in an App Device, credit cards can be tapped instead of manually entered
 */
@Injectable({providedIn: "root"})
export class TapToPayService implements DeferredInjectable {

    private appDeviceService: AppDeviceService = inject(AppDeviceService);
    private orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private stripeConnectionTokenRefreshService: StripeConnectionTokenRefreshService = inject(StripeConnectionTokenRefreshService);
    private tapToPayIntentCreationService: TapToPayIntentCreationService = inject(TapToPayIntentCreationService);
    private authorisedPaymentEntityModel: AuthorisedPaymentEntityModel = inject(AuthorisedPaymentEntityModel);
    private notificationService: NotificationService;

    // Enable server-side intent creation. If this flag is set to false, the payment intent is created on the client-side.
    // Note: as of server-side API version 29.2.0, manual_preferred payment capture method is not yet supported.
    private static readonly USE_SERVER_SIDE_INTENT_CREATION: boolean = false;

    /**
     * Indicates that tap-to-pay payments are available
     */
    public readonly isAvailable: Signal<boolean> = computed<boolean>(() => {
        return this.appDeviceService.isAvailable();
    });

    constructor() {
        console.log("--- TapToPayService");

        // Handle unsolicited payment error from the App Device
        this.appDeviceService.registerMessageHandler(ReceivedMessageType.TAP_TO_PAY_ERROR, async (type: ReceivedMessageType, tapToPayError: TapToPayError) =>
            this.notificationService.logErrorToServer("Tap to pay error reported by app device", undefined, tapToPayError));

        // Handle Stripe Connection Token Refresh messages from the App Device
        this.appDeviceService.registerMessageHandler(ReceivedMessageType.STRIPE_TOKEN_REFRESH, async () => {
            const request: StripeConnectionTokenRefreshRequestData = EntityData.create(StripeConnectionTokenRefreshRequestData);
            request.deviceSecret = this.appDeviceService.deviceSecret();
            request.deviceSerialNumber = this.appDeviceService.deviceSerialNumber();
            return this.stripeConnectionTokenRefreshService.performAction(request);
        });

        // Handle Payment Intent Notification messages from the App Device
        // Note: these are unused at this time. Possible future use to allow reuse of the payment intent.
        // noinspection JSUnusedLocalSymbols
        this.appDeviceService.registerMessageHandler(ReceivedMessageType.PAYMENT_INTENT_NOTIFICATION,
            async (type: ReceivedMessageType, tapToPayIntent: TapToPayIntent) => new AppDeviceMessageAck());

        // Handle Payment Method Notification messages from the App Device
        this.appDeviceService.registerMessageHandler(ReceivedMessageType.PAYMENT_METHOD_NOTIFICATION,
            async (type: ReceivedMessageType, authorisedPayment: AuthorisedTapToPayPayment) => {
                this.authorisedPaymentEntityModel.newEntity();
                this.authorisedPaymentEntityModel.data.assign(authorisedPayment);
                this.authorisedPaymentEntityModel.data.storeId = this.orderingContextModel.currentStore().storeId;
                this.authorisedPaymentEntityModel.data.integratedPaymentConfigId = 0;
                this.authorisedPaymentEntityModel.data.platformLiveMode = true;
                this.authorisedPaymentEntityModel.data.firstName = this.orderingContextModel.trimmedFirstName();
                this.authorisedPaymentEntityModel.data.lastName = this.orderingContextModel.trimmedLastName();
                this.authorisedPaymentEntityModel.data.email = this.orderingContextModel.email();
                this.authorisedPaymentEntityModel.data.phoneNumber = this.orderingContextModel.mobilePhone();

                try {
                    await this.authorisedPaymentEntityModel.saveEntity();
                    return new AppDeviceMessageAck();
                } catch (error) {
                    console.error("Failed to save authorised payment", error);
                    this.notificationService.logErrorToServer("Failed to save authorised payment", authorisedPayment, error);
                    throw {
                        errorType: AppDeviceErrorType.CHEWZIE_ERROR,
                        message: "Failed to save authorised payment"
                    };
                }
            });
    }

    public inject(): void {
        this.notificationService = inject(NotificationService);
    }

    /**
     * Requests the app device to authorise the specified payment authorisation.
     */
    public async authorisePayment(paymentAuthorisation: PaymentAuthorisationData): Promise<AuthorisedTapToPayPayment> {
        let tapToPayPaymentAuthorisation: TapToPayPaymentAuthorisationData = <TapToPayPaymentAuthorisationData>EntityData.create(TapToPayPaymentAuthorisationData);
        tapToPayPaymentAuthorisation.assign(paymentAuthorisation);
        tapToPayPaymentAuthorisation.amount = paymentAuthorisation.amount + (paymentAuthorisation.tipAmount || 0);
        tapToPayPaymentAuthorisation.currency = "AUD";
        tapToPayPaymentAuthorisation.firstName = this.orderingContextModel.trimmedFirstName();
        tapToPayPaymentAuthorisation.lastName = this.orderingContextModel.trimmedLastName();
        tapToPayPaymentAuthorisation.email = this.orderingContextModel.email();
        tapToPayPaymentAuthorisation.phoneNumber = this.orderingContextModel.mobilePhone();
        tapToPayPaymentAuthorisation.storeName = this.orderingContextModel.currentStore().storeName;
        tapToPayPaymentAuthorisation.venueId = this.orderingContextModel.currentStore().venueId;
        tapToPayPaymentAuthorisation.clientId = this.configurationModel.clientId();

        if (TapToPayService.USE_SERVER_SIDE_INTENT_CREATION) {
            try {
                tapToPayPaymentAuthorisation = await this.tapToPayIntentCreationService.performAction(tapToPayPaymentAuthorisation);
            } catch (error) {
                console.error("Failed to perform server-side tap-to-pay payment intent creation. Falling back to client-side creation", error);
            }
        }

        return this.appDeviceService.sendMessage(SentMessageType.PAYMENT_AUTHORISATION, tapToPayPaymentAuthorisation);
    }
}
